import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, MapPin, Package, Book } from 'lucide-react';
import { PhoneMockup } from '../../components/PhoneMockup';
import { QRPageFooter } from '../../components/qr/QRPageFooter';

const ShopDemo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const isDemo = new URLSearchParams(location.search).get('demo') === 'true';
  
  useEffect(() => {
    if (isDemo) {
      window.history.replaceState({}, '', '/qr/demo-shop');
    }
  }, [isDemo]);

  const handleBackToDemos = () => {
    navigate('/');
    setTimeout(() => {
      const demosSection = document.getElementById('demos');
      if (demosSection) {
        const sectionIndex = 7; // Demos is the 8th section (0-indexed)
        window.scrollTo({
          top: sectionIndex * window.innerHeight,
          behavior: 'smooth'
        });
      }
    }, 100);
  };

  // Sample data for a traditional cookbook
  const bookData = {
    bookTitle: 'Escoffier und die Nouvelle Cuisine',
    author: 'Auguste Escoffier',
    isbn: '978-3-86820-344-7',
    coverUrl: '/images/escoffier-und-die-nouvelle-cuisine-1-auflage-pfanneberg.webp',
    description: 'Das Standardwerk der französischen Haute Cuisine. Auguste Escoffier revolutionierte die Kochkunst und legte den Grundstein für die moderne Gastronomie. Mit über 500 klassischen Rezepten und Techniken.',
    shops: [
      { 
        name: 'Amazon', 
        url: '#', 
        price: '29.95', 
        currency: 'EUR', 
        availability: 'in_stock' as const,
        isLocal: false
      },
      { 
        name: 'Thalia', 
        url: '#', 
        price: '29.95', 
        currency: 'EUR', 
        availability: 'in_stock' as const,
        isLocal: false
      },
      { 
        name: 'Buchhandlung am Markt', 
        url: '#', 
        price: '29.95', 
        currency: 'EUR', 
        availability: 'in_stock' as const,
        isLocal: true
      },
      { 
        name: 'Hugendubel', 
        url: '#', 
        price: '28.99', 
        currency: 'EUR', 
        availability: 'in_stock' as const,
        isLocal: false
      },
      { 
        name: 'Weltbild', 
        url: '#', 
        price: '27.99', 
        currency: 'EUR', 
        availability: 'out_of_stock' as const,
        isLocal: false
      }
    ]
  };

  const brandingData = {
    companyName: 'Max Mustermann GmbH',
    logoUrl: null,
    primaryColor: '#3B82F6',
    displayType: 'name_only' as const
  };

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8 text-center">
          {isDemo && (
            <button
              onClick={handleBackToDemos}
              className="mb-4 text-blue-600 hover:text-blue-700 transition-colors text-sm"
            >
              ← Zurück zu Demos
            </button>
          )}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Multi-Shop Demo</h1>
          <p className="text-gray-600">So sieht Ihre Multi-Shop-Seite auf dem Smartphone aus</p>
        </div>

        {/* Phone Mockup with Multi-Shop Page */}
        <PhoneMockup className="mb-8">
          <div className="min-h-screen bg-gray-50">
            {/* Header with Branding */}
            <div className="bg-white shadow-sm">
              <div className="container mx-auto px-4 py-3">
                <div className="flex items-center justify-center">
                  <span className="text-base font-semibold" style={{ color: brandingData.primaryColor }}>
                    {brandingData.companyName}
                  </span>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-4 py-4">
              {/* Book Info */}
              <div className="bg-white rounded-2xl shadow-xl p-4 mb-4">
                <div className="flex gap-3 mb-3">
                  {bookData.coverUrl ? (
                    <img 
                      src={bookData.coverUrl} 
                      alt={bookData.bookTitle}
                      className="w-20 h-28 rounded-lg shadow-lg object-cover"
                    />
                  ) : (
                    <div className="w-20 h-28 bg-gray-200 rounded-lg shadow-lg flex items-center justify-center">
                      <Book className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h1 className="text-base font-bold text-gray-900 mb-1">
                      {bookData.bookTitle}
                    </h1>
                    <p className="text-sm text-gray-600 mb-2">von {bookData.author}</p>
                    {bookData.isbn && (
                      <p className="text-xs text-gray-500">ISBN: {bookData.isbn}</p>
                    )}
                  </div>
                </div>
                {bookData.description && (
                  <p className="text-xs text-gray-700 leading-relaxed">{bookData.description}</p>
                )}
              </div>

              {/* Shop Selection */}
              <div className="bg-white rounded-2xl shadow-xl p-4">
                <h2 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" style={{ color: brandingData.primaryColor }} />
                  Wählen Sie Ihren Buchhändler
                </h2>

                {/* Shop List */}
                <div className="space-y-2">
                  {bookData.shops.map((shop, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 rounded-xl p-3"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-white rounded-lg shadow-sm flex items-center justify-center">
                            {shop.isLocal ? (
                              <MapPin className="w-5 h-5 text-green-600" />
                            ) : (
                              <Package className="w-5 h-5 text-gray-600" />
                            )}
                          </div>
                          <div>
                            <h3 className="font-semibold text-sm text-gray-900">
                              {shop.name}
                            </h3>
                            {shop.availability === 'in_stock' && (
                              <p className="text-xs text-green-600">Sofort lieferbar</p>
                            )}
                            {shop.availability === 'out_of_stock' && (
                              <p className="text-xs text-red-600">Derzeit nicht verfügbar</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {shop.price && (
                            <span className="text-sm font-bold" style={{ color: brandingData.primaryColor }}>
                              {shop.price} {shop.currency === 'EUR' ? '€' : shop.currency}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Price Comparison Note */}
                <div className="mt-4 text-center text-xs text-gray-600">
                  <p>Preise können variieren. Wir zeigen unverbindliche Preisempfehlungen.</p>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-4">
                <QRPageFooter 
                  branding={{ primaryColor: brandingData.primaryColor }} 
                  className="text-gray-600 text-xs" 
                />
              </div>
            </div>
          </div>
        </PhoneMockup>

        {/* Demo Info */}
        {isDemo && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
              <h3 className="text-lg font-semibold mb-2 text-blue-900">Demo-Hinweis</h3>
              <p className="text-sm text-blue-800 mb-2">
                Dies ist eine Demo einer Multi-Shop-Seite mit dem Branding "Max Mustermann GmbH". 
                Die Seite zeigt "Die Deutsche Küche - Traditionelle Rezepte" als Beispiel.
              </p>
              <p className="text-sm text-blue-800">
                Mit BeyondPages können Sie Multi-Shop-Links erstellen, die Ihre Leser zu allen 
                wichtigen Händlern führen. Sie können die Händlerliste jederzeit anpassen – 
                auch nach dem Druck des QR-Codes.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopDemo;